import { useMemo } from 'react';
import {
  parseDate,
  isValidDate,
  calculateSLAStatus,
  formatTimeRemaining,
  formatResponseTime,
  formatPriority,
} from '../../../utils/slaDashboardUtils';
import { AT_RISK_HOURS_THRESHOLD } from '../../../constants/slaDashboard';
import { formatQueryProjectNameForDisplay } from '../../../utils/projectsUtils';

/**
 * Hook to process queries for table display with pagination
 * @param {Array} queries - Array of queries
 * @param {Date} now - Current date
 * @param {number} page - Current page
 * @param {number} rowsPerPage - Rows per page
 * @returns {Object} Processed queries and pagination data
 */
export const useSLAQueriesTable = (queries, now, page, rowsPerPage, projectsCatalog = []) => {
  const queriesWithSLA = useMemo(() => {
    if (!queries || queries.length === 0) {
      return [];
    }

    const catalog = Array.isArray(projectsCatalog) ? projectsCatalog : [];

    const formatted = queries
      .filter((query) => query.slaDueDate)
      .map((query) => {
        const due = parseDate(query.slaDueDate);
        // Skip invalid dates
        if (!due || !isValidDate(due)) {
          return null;
        }

        const respondedAt = parseDate(query.respondedAt);
        const closedAt = parseDate(query.closedAt);
        const completedAt = respondedAt || closedAt;
        
        const status = calculateSLAStatus(due, completedAt, now, AT_RISK_HOURS_THRESHOLD);

        const nowTime = now.getTime();
        const dueTime = due.getTime();
        const hoursUntilDue = (dueTime - nowTime) / (1000 * 60 * 60);

        let timeRemaining = 'N/A';
        if (!completedAt) {
          timeRemaining = formatTimeRemaining(hoursUntilDue);
        }

        const responseTime = formatResponseTime(respondedAt, parseDate(query.raisedAt));

        return {
          id: query.queryId || query._id,
          title: query.queryHeader,
          priority: formatPriority(query.priority),
          project:
            formatQueryProjectNameForDisplay(query.projectName, catalog) ||
            query.projectName ||
            'Unknown',
          status,
          slaDue: due.toLocaleString(),
          timeRemaining,
          responseTime,
          rawDue: due.getTime(),
        };
      })
      .filter((query) => query !== null) // Remove invalid entries
      .sort((a, b) => a.rawDue - b.rawDue);

    return formatted;
  }, [queries, now, projectsCatalog]);

  const paginatedData = useMemo(
    () => queriesWithSLA.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [queriesWithSLA, page, rowsPerPage]
  );

  return {
    queriesWithSLA,
    paginatedData,
  };
};
